import React, { createContext, useState, useCallback, useRef, useEffect } from 'react';
import {
  ROOMS,
  EMAIL_TEMPLATES_EVENTS,
  EMAIL_TEMPLATES_WEDDINGS,
} from '../lib/sampleData';
import { REAL_EVENTS, REAL_CLIENTS, REAL_PAYMENTS } from '../lib/realData';
import { getWorkflowForStageChange, createPendingEmailAction, SCHEDULED_WORKFLOWS } from '../lib/workflowEngine';
import { sendEmail, buildVariables, replaceVariables, generateBrandedEmail, fetchInboundEmails as triggerInboundFetch } from '../lib/emailService';
import { SAMPLE_USERS, isNotificationRelevant, getRoleColour } from '../lib/userSystem';
import { todayKey } from '../lib/dateHelpers';
import { getEventDetailStatus, detailTaskDueDate, DETAIL_TASK_TYPE } from '../lib/eventCompleteness';
import { useAuth } from './AuthContext';
import {
  isSupabaseReady,
  fetchEvents, insertEvent as dbInsertEvent, updateEventDb, deleteEventDb, subscribeToEvents,
  fetchClients, insertClient as dbInsertClient, updateClientDb, deleteClientDb,
  fetchPayments, insertPayment as dbInsertPayment, updatePaymentDb, deletePaymentDb,
  fetchSentEmails, insertSentEmail as dbInsertSentEmail, updateSentEmailDb,
  fetchInboundEmails, markInboundEmailRead, linkInboundEmailToEvent, linkInboundEmailToBooking, archiveInboundEmail,
  fetchActivityLog, insertActivity as dbInsertActivity,
  fetchTableBookings, insertTableBooking as dbInsertTableBooking,
  updateTableBookingDb, deleteTableBookingDb, subscribeToTableBookings,
  fetchEventTasks, insertEventTask as dbInsertEventTask,
  updateEventTaskDb, deleteEventTaskDb,
  fetchSpaBookings, insertSpaBooking as dbInsertSpaBooking,
  updateSpaBookingDb, deleteSpaBookingDb, subscribeToSpaBookings,
  fetchRestaurantPackages, insertRestaurantPackage as dbInsertRestaurantPackage,
  updateRestaurantPackage as dbUpdateRestaurantPackage,
  seedFromRealData,
} from '../lib/supabaseService';

// Debug helper — only logs in development
const dbLog = (...args) => {
  if (process.env.NODE_ENV === 'development' || window.__ERRIGAL_DEBUG) {
    console.log(...args);
  }
};

/**
 * AppContext - Global state management for the Errigal Events Manager
 * Now backed by Supabase for persistence — data survives page refreshes.
 * Falls back to in-memory realData.js when Supabase is not configured.
 */
export const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
  // ─── USER SESSION ───
  const { profile: authProfile, isAuthenticated, isSupabaseConfigured, loading: authLoading, fetchAllProfiles } = useAuth();

  const [demoUser, setDemoUser] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_current_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return SAMPLE_USERS.find((u) => u.id === parsed.id) || SAMPLE_USERS[0];
      }
      return SAMPLE_USERS[0];
    } catch { return SAMPLE_USERS[0]; }
  });

  const currentUser = (isSupabaseConfigured && isAuthenticated && authProfile)
    ? { id: authProfile.id, name: authProfile.name, initials: authProfile.initials, role: authProfile.role }
    : demoUser;

  const switchUser = useCallback((userId) => {
    if (isSupabaseConfigured && isAuthenticated) return;
    const user = SAMPLE_USERS.find((u) => u.id === userId);
    if (user) {
      setDemoUser(user);
      try { localStorage.setItem('errigal_current_user', JSON.stringify(user)); } catch {}
    }
  }, [isSupabaseConfigured, isAuthenticated]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      try { localStorage.setItem('errigal_current_user', JSON.stringify(demoUser)); } catch {}
    }
  }, [demoUser, isSupabaseConfigured]);

  // ─── AVAILABLE USERS (for @mention etc.) ───
  const [availableUsers, setAvailableUsers] = useState(SAMPLE_USERS);

  // Load real profiles from Supabase when authenticated
  useEffect(() => {
    if (!isSupabaseConfigured || !isAuthenticated || !fetchAllProfiles) return;
    let cancelled = false;
    (async () => {
      try {
        const profiles = await fetchAllProfiles();
        if (cancelled || !profiles || profiles.length === 0) return;
        const mapped = profiles
          .filter((p) => p.is_active !== false)
          .map((p) => ({
            id: p.id,
            name: p.name || p.email?.split('@')[0] || 'Unknown',
            role: p.role || 'reception',
            initials: p.initials || (p.name ? p.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?'),
            email: p.email || '',
          }));
        setAvailableUsers(mapped);
      } catch (err) {
        console.warn('[AppContext] Failed to load profiles for @mentions, using SAMPLE_USERS:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isSupabaseConfigured, isAuthenticated, fetchAllProfiles]);

  // Personal tasks & quick notes were removed (Oct 2026). They only ever
  // lived in this browser's storage — clear what's left behind.
  useEffect(() => {
    try { localStorage.removeItem('errigal_personal_tasks'); localStorage.removeItem('errigal_personal_notes'); } catch { /* storage unavailable */ }
  }, []);

  // ─── NOTIFICATIONS (role-filtered) ───
  const [allNotifications, setAllNotifications] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_notifications');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem('errigal_notifications', JSON.stringify(allNotifications)); } catch {}
  }, [allNotifications]);

  const notifications = allNotifications.filter(
    (n) => !n.type || isNotificationRelevant(n.type, currentUser?.role)
  );

  const addNotification = useCallback((notification) => {
    const notif = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      ...notification,
      read: false,
      createdAt: new Date().toISOString(),
      time: new Date().toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }),
    };
    setAllNotifications((prev) => [notif, ...prev].slice(0, 100));
  }, []);

  // ─── ACTIVITY LOG ───
  const [activityLog, setActivityLog] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_activity_log');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem('errigal_activity_log', JSON.stringify(activityLog)); } catch {}
  }, [activityLog]);

  const logActivity = useCallback((action) => {
    const entry = {
      userId: currentUser?.id,
      userName: currentUser?.name || 'Unknown',
      userInitials: currentUser?.initials || '?',
      colour: getRoleColour(currentUser?.role),
      action,
      time: new Date().toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }) +
            ' · ' + new Date().toLocaleDateString('en-IE', { day: 'numeric', month: 'short' }),
      timestamp: new Date().toISOString(),
    };
    setActivityLog((prev) => [entry, ...prev].slice(0, 200));

    // Also persist to Supabase if available
    if (useDb) {
      dbInsertActivity(entry).catch((err) =>
        console.warn('[Supabase] Failed to log activity:', err.message)
      );
    }
  }, [currentUser]);

  // ─── DATABASE MODE ───
  // Determine once whether we should use Supabase or fall back to in-memory
  const useDb = isSupabaseReady();
  const [dbLoading, setDbLoading] = useState(useDb); // true while fetching initial data
  const [dbError, setDbError] = useState(null);
  const dbInitDone = useRef(false);

  // ─── CORE APP STATE ───
  // Initialise with realData as fallback; if DB is available these get overwritten on mount
  const [events, setEvents] = useState(useDb ? [] : REAL_EVENTS);
  const [clients, setClients] = useState(useDb ? [] : REAL_CLIENTS);
  const [tableBookings, setTableBookings] = useState([]);
  const [payments, setPayments] = useState(useDb ? [] : REAL_PAYMENTS);
  const [invoices, setInvoices] = useState([]);
  const [eventTasks, setEventTasks] = useState([]);
  const [spaBookings, setSpaBookings] = useState([]);
  // Restaurant packages (VIP, Brunch, Afternoon Tea...) — editable list, see migration 023
  const [restaurantPackages, setRestaurantPackages] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([
    ...EMAIL_TEMPLATES_EVENTS,
    ...EMAIL_TEMPLATES_WEDDINGS,
  ]);

  // ─── LOAD FROM SUPABASE ON MOUNT ───
  // IMPORTANT: Wait until auth is resolved (not loading) AND user is authenticated.
  // Supabase RLS policies require an authenticated session — if we query before
  // auth is ready, RLS silently returns empty results and inserts are dropped.

  // Retry helper — allows user to retry if initial load fails
  const retryLoadData = useCallback(() => {
    dbInitDone.current = false;
    setDbLoading(true);
    setDbError(null);
    // Trigger the effect by bumping a counter
    setRetryCount((c) => c + 1);
  }, []);

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!useDb) return;                 // Not using Supabase at all
    if (authLoading) return;            // Auth still initialising — wait
    if (!isAuthenticated) return;       // Not logged in yet — wait
    if (dbInitDone.current) return;     // Already loaded — skip
    dbInitDone.current = true;

    // Timeout: if data hasn't loaded in 15s, fall back to local data
    const timeout = setTimeout(() => {
      if (dbLoading) {
        console.warn('[Supabase] Data load timed out after 15s — using local data');
        setDbError('Connection timed out');
        setEvents(REAL_EVENTS);
        setClients(REAL_CLIENTS);
        setPayments(REAL_PAYMENTS);
        setDbLoading(false);
        dbInitDone.current = false; // Allow retry on next attempt
      }
    }, 15000);

    async function loadData() {
      try {
        console.log('[Supabase] Auth ready — loading data...');

        // Seed if database is empty
        await seedFromRealData(REAL_EVENTS, REAL_CLIENTS);

        // Fetch all data in parallel
        const [dbEvents, dbClients, dbPayments, dbSentEmails, dbInboundEmails, dbActivity, dbTableBookings, dbEventTasks, dbSpaBookings] = await Promise.all([
          fetchEvents(),
          fetchClients(),
          fetchPayments(),
          fetchSentEmails().catch(() => []),
          fetchInboundEmails().catch(() => []),
          fetchActivityLog().catch(() => []),
          fetchTableBookings().catch(() => []),
          fetchEventTasks().catch(() => []),
          fetchSpaBookings().catch(() => []),
        ]);

        clearTimeout(timeout);
        setEvents(dbEvents);
        setClients(dbClients);
        setPayments(dbPayments);
        if (dbSentEmails.length > 0) setSentEmails(dbSentEmails);
        if (dbInboundEmails.length > 0) {
          setInboundEmails(dbInboundEmails);
          setUnreadEmailCount(dbInboundEmails.filter(e => !e.is_read).length);
        }
        if (dbActivity.length > 0) setActivityLog(dbActivity);
        if (dbTableBookings.length > 0) setTableBookings(dbTableBookings);
        if (dbEventTasks.length > 0) setEventTasks(dbEventTasks);
        if (dbSpaBookings.length > 0) setSpaBookings(dbSpaBookings);
        // Packages load separately so a missing table never blocks the main load
        fetchRestaurantPackages()
          .then((pk) => setRestaurantPackages(pk))
          .catch((e) => console.warn('[Supabase] Could not load restaurant packages:', e.message));

        console.log(`[Supabase] Loaded ${dbEvents.length} events, ${dbClients.length} clients, ${dbPayments.length} payments, ${dbInboundEmails.length} inbound emails, ${dbTableBookings.length} table bookings, ${dbEventTasks.length} tasks, ${dbSpaBookings.length} spa bookings`);
      } catch (err) {
        clearTimeout(timeout);
        console.error('[Supabase] Failed to load data, falling back to local:', err.message);
        setDbError(err.message);
        // Fall back to local data
        setEvents(REAL_EVENTS);
        setClients(REAL_CLIENTS);
        setPayments(REAL_PAYMENTS);
      } finally {
        clearTimeout(timeout);
        setDbLoading(false);
      }
    }

    loadData();

    return () => clearTimeout(timeout);
  }, [useDb, authLoading, isAuthenticated, retryCount]);

  // LIVE TABLE BOOKINGS: initial load fetches once. Without this an open
  // reception session never sees bookings made elsewhere (esp. the public
  // widget) until reload, so a booking made during service is missing from the
  // grid and printed function sheet even though it's in the DB. Handlers dedupe
  // by id so an optimistic local insert isn't duplicated when its INSERT echoes.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const unsubscribe = subscribeToTableBookings({
      onInsert: (booking) => {
        setTableBookings((prev) =>
          prev.some((b) => b.id === booking.id) ? prev : [...prev, booking]
        );
      },
      onUpdate: (booking) => {
        setTableBookings((prev) =>
          prev.map((b) => (b.id === booking.id ? { ...b, ...booking } : b))
        );
      },
      onDelete: (bookingId) => {
        setTableBookings((prev) => prev.filter((b) => b.id !== bookingId));
      },
    });
    return unsubscribe;
  }, [useDb, authLoading, isAuthenticated]);

  // SAFETY NET for table bookings: Realtime only helps if the table is
  // actually in the supabase_realtime publication on this project (migration
  // 021) and the socket stays connected. If either of those silently isn't
  // true, an already-open session would otherwise never see a booking made
  // elsewhere until a full page reload — which is how a booking can look
  // "lost" even though it's safely in the database. Re-fetch and reconcile
  // periodically so a missed realtime event can't hide a booking for an
  // entire shift.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await fetchTableBookings();
        const freshIds = new Set(fresh.map((b) => b.id));
        setTableBookings((prev) => {
          // Keep any bookings this tab has locally that the server doesn't
          // know about yet (there shouldn't be any now that failed inserts
          // aren't added to state, but this is harmless if one ever slips
          // through) — otherwise the DB is the source of truth.
          const localOnly = prev.filter((b) => !freshIds.has(b.id));
          return [...fresh, ...localOnly];
        });
      } catch (err) {
        console.error('[Supabase] table bookings safety-net refresh failed:', err.message);
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [useDb, authLoading, isAuthenticated]);

  // LIVE EVENTS: same rationale as the table-bookings block above — an event
  // edited on one device (guest count, dietary requirements, stage, a
  // reception adding a note) was previously invisible on another open
  // session, including an already-open printed function sheet, until a
  // manual reload. Dedupes by id so an optimistic local insert isn't
  // duplicated when its own INSERT echoes back.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const unsubscribe = subscribeToEvents({
      onInsert: (event) => {
        setEvents((prev) =>
          prev.some((e) => e.id === event.id) ? prev : [...prev, event]
        );
      },
      onUpdate: (event) => {
        setEvents((prev) =>
          prev.map((e) => (e.id === event.id ? { ...e, ...event } : e))
        );
      },
      onDelete: (eventId) => {
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
      },
    });
    return unsubscribe;
  }, [useDb, authLoading, isAuthenticated]);

  // SAFETY NET for events — same reasoning as the table-bookings safety net:
  // realtime only helps if the table stays in the publication and the socket
  // stays connected. Re-fetch and reconcile periodically so a missed event
  // can't hide a change for an entire shift.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await fetchEvents();
        const freshIds = new Set(fresh.map((e) => e.id));
        setEvents((prev) => {
          const localOnly = prev.filter((e) => !freshIds.has(e.id));
          return [...fresh, ...localOnly];
        });
      } catch (err) {
        console.error('[Supabase] events safety-net refresh failed:', err.message);
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [useDb, authLoading, isAuthenticated]);

  // LIVE SPA BOOKINGS: spa bookings feed the printed function sheets too, so
  // the same live-sync gap applied here — a spa booking added elsewhere was
  // missing from an already-open sheet until reload.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const unsubscribe = subscribeToSpaBookings({
      onInsert: (booking) => {
        setSpaBookings((prev) =>
          prev.some((b) => b.id === booking.id) ? prev : [...prev, booking]
        );
      },
      onUpdate: (booking) => {
        setSpaBookings((prev) =>
          prev.map((b) => (b.id === booking.id ? { ...b, ...booking } : b))
        );
      },
      onDelete: (bookingId) => {
        setSpaBookings((prev) => prev.filter((b) => b.id !== bookingId));
      },
    });
    return unsubscribe;
  }, [useDb, authLoading, isAuthenticated]);

  // SAFETY NET for spa bookings — same reasoning as above.
  useEffect(() => {
    if (!useDb) return;
    if (authLoading || !isAuthenticated) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await fetchSpaBookings();
        const freshIds = new Set(fresh.map((b) => b.id));
        setSpaBookings((prev) => {
          const localOnly = prev.filter((b) => !freshIds.has(b.id));
          return [...fresh, ...localOnly];
        });
      } catch (err) {
        console.error('[Supabase] spa bookings safety-net refresh failed:', err.message);
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [useDb, authLoading, isAuthenticated]);

  // ═══ AUTO-ARCHIVE (DISABLED 2026-04-19) ═══
  // Previously this effect silently archived every event whose date had passed,
  // which made past events disappear from Room Diary and the Events list.
  // Ronan wants past events to remain visible as a historical record, so the
  // auto-archive is disabled. Archiving is still available manually by editing
  // an event's stage to 'archived'. If we ever need a scheduled cleanup, put
  // it behind a user-triggered button rather than a silent on-load effect.

  // ─── EMAIL POLLING (check for new emails every 2 minutes) ───
  useEffect(() => {
    if (!useDb) return; // Only poll when connected to Supabase

    const pollEmails = async () => {
      try {
        const emails = await fetchInboundEmails();
        const prevCount = inboundEmails.length;
        setInboundEmails(emails);
        const unread = emails.filter(e => !e.is_read).length;
        setUnreadEmailCount(unread);

        // Check for NEW emails (not just unread)
        if (emails.length > prevCount) {
          const newEmails = emails.slice(0, emails.length - prevCount);
          newEmails.forEach(email => {
            addNotification({
              title: `New email from ${email.from_name || email.from_address || 'Unknown'}`,
              type: 'new_email',
              message: email.subject || 'No subject',
            });
          });
        }
      } catch (err) {
        console.warn('[Email Poll] Failed:', err.message);
      }
    };

    // Initial fetch
    pollEmails();

    // Poll every 2 minutes
    const interval = setInterval(pollEmails, 120000);
    return () => clearInterval(interval);
  }, [useDb]); // eslint-disable-line react-hooks/exhaustive-deps

  // Email automation state
  const [sentEmails, setSentEmails] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_sent_emails');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [emailToasts, setEmailToasts] = useState([]);
  const [workflowsEnabled, setWorkflowsEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_workflows_enabled');
      return stored !== null ? JSON.parse(stored) : true;
    } catch { return true; }
  });
  const pendingTimers = useRef({});

  const [emailTracking, setEmailTracking] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_email_tracking');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  const [eventEmailOverrides, setEventEmailOverrides] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_event_email_overrides');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  const [emailNotifications, setEmailNotifications] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);

  // Inbound emails from Supabase
  const [inboundEmails, setInboundEmails] = useState([]);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);

  // Persist to localStorage (still useful as local cache)
  useEffect(() => {
    try { localStorage.setItem('errigal_sent_emails', JSON.stringify(sentEmails)); } catch {}
  }, [sentEmails]);

  useEffect(() => {
    try { localStorage.setItem('errigal_workflows_enabled', JSON.stringify(workflowsEnabled)); } catch {}
  }, [workflowsEnabled]);

  useEffect(() => {
    try { localStorage.setItem('errigal_email_tracking', JSON.stringify(emailTracking)); } catch {}
  }, [emailTracking]);

  useEffect(() => {
    try { localStorage.setItem('errigal_event_email_overrides', JSON.stringify(eventEmailOverrides)); } catch {}
  }, [eventEmailOverrides]);

  // Calculate upcoming reminders
  useEffect(() => {
    const reminders = [];
    events.forEach((event) => {
      SCHEDULED_WORKFLOWS.forEach((workflow) => {
        const overrideKey = `${event.id}_${workflow.id}`;
        if (eventEmailOverrides[overrideKey]?.disabled) return;
        try { if (!workflow.condition(event)) return; } catch { return; }
        const alreadySent = sentEmails.some(
          (email) => email.eventId === event.id && email.templateId === workflow.templateId
        );
        if (alreadySent) return;
        const template = emailTemplates.find((t) => t.id === workflow.templateId);
        const eventDate = new Date(event.date);
        const dueDate = workflow.daysBefore
          ? new Date(eventDate.getTime() - workflow.daysBefore * 24 * 60 * 60 * 1000)
          : workflow.daysAfterStageChange
          ? new Date(Date.now() + workflow.daysAfterStageChange * 24 * 60 * 60 * 1000)
          : new Date();
        reminders.push({
          id: overrideKey,
          workflowId: workflow.id,
          eventId: event.id,
          eventName: event.name,
          client: event.client,
          templateId: workflow.templateId,
          templateName: template?.name || workflow.label,
          description: workflow.description,
          dueDate: dueDate.toISOString(),
          eventDate: event.date,
          type: 'scheduled',
        });
      });
    });
    setUpcomingReminders(reminders.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)));
  }, [events, sentEmails, emailTemplates, eventEmailOverrides]);

  // ═══════════════════════════════════════════
  // CRUD OPERATIONS — now persist to Supabase
  // ═══════════════════════════════════════════

  /**
   * Add a new event — inserts into Supabase if available, always updates local state
   */
  const addEvent = useCallback(async (eventData) => {
    const baseEvent = {
      ...eventData,
      stage: eventData.stage || 'enquiry',
      depositPaid: eventData.depositPaid || 0,
    };

    let newEvent;
    if (useDb) {
      try {
        newEvent = await dbInsertEvent(baseEvent);
      } catch (err) {
        console.error('[Supabase] insertEvent failed:', err.message);
        throw new Error(err.message || 'Failed to save event to database');
      }
    } else {
      newEvent = { id: `evt${String(events.length + 1).padStart(3, '0')}`, ...baseEvent };
    }

    setEvents((prev) => [...prev, newEvent]);
    logActivity(`created new event "${eventData.name || 'Untitled'}"`);
    addNotification({ title: `New event: ${eventData.name || 'Untitled'}`, type: 'new_enquiry', message: `${eventData.name} added to pipeline` });
    return newEvent;
  }, [useDb, events.length, logActivity, addNotification]);

  /**
   * Update an existing event
   */
  const updateEvent = useCallback(async (eventId, updates) => {
    // Capture previous state for rollback
    let previousEvent = null;
    setEvents((prev) => {
      previousEvent = prev.find((e) => e.id === eventId);
      return prev.map((evt) => (evt.id === eventId ? { ...evt, ...updates } : evt));
    });

    // Persist to DB
    if (useDb) {
      try {
        await updateEventDb(eventId, updates);
      } catch (err) {
        console.error('[Supabase] updateEvent failed:', err.message);
        // Rollback optimistic update
        if (previousEvent) {
          setEvents((prev) =>
            prev.map((evt) => (evt.id === eventId ? previousEvent : evt))
          );
        }
        throw new Error(err.message || 'Failed to save changes to database');
      }
    }

    return { id: eventId, ...updates };
  }, [useDb]);

  /**
   * Update only the stage of an event
   */
  const updateEventStage = useCallback(async (eventId, newStage) => {
    let previousStageValue = null;
    setEvents((prev) => {
      const evt = prev.find((e) => e.id === eventId);
      if (evt) {
        previousStageValue = evt.stage;
        logActivity(`moved "${evt.name}" to ${newStage.replace(/_/g, ' ')}`);
        addNotification({ title: `${evt.name} → ${newStage.replace(/_/g, ' ')}`, type: 'stage_change', message: `Stage updated by ${currentUser?.name}` });
      }
      return prev.map((e) => (e.id === eventId ? { ...e, stage: newStage } : e));
    });

    if (useDb) {
      try {
        await updateEventDb(eventId, { stage: newStage });
      } catch (err) {
        console.error('[Supabase] updateEventStage failed:', err.message);
        // Rollback
        if (previousStageValue) {
          setEvents((prev) =>
            prev.map((e) => (e.id === eventId ? { ...e, stage: previousStageValue } : e))
          );
        }
        throw new Error(err.message || 'Failed to update stage in database');
      }
    }

    return { id: eventId, stage: newStage };
  }, [useDb, logActivity, addNotification, currentUser?.name]);

  /**
   * Delete an event
   */
  const deleteEvent = useCallback(async (eventId) => {
    const existingEvent = events.find((e) => e.id === eventId);
    setEvents((prev) => {
      const evt = prev.find((e) => e.id === eventId);
      if (evt) logActivity(`deleted event "${evt.name}"`);
      return prev.filter((e) => e.id !== eventId);
    });

    if (useDb) {
      try {
        await deleteEventDb(eventId);
      } catch (err) {
        console.error('[Supabase] deleteEvent failed:', err.message);
        // Rollback — an event that failed to delete (e.g. blocked by a
        // payments/tasks foreign key) must not vanish locally while it
        // still exists in the database, only to reappear unexplained on
        // the next reload.
        if (existingEvent) {
          setEvents((prev) => [...prev, existingEvent]);
        }
        // Preserve the Postgres error code (23503 = foreign_key_violation,
        // e.g. payments_event_id_fkey — see migration 014) so the caller
        // can show a plain-English reason instead of the raw DB message.
        const wrapped = new Error(err.message || 'Failed to delete event from database');
        wrapped.code = err.code;
        throw wrapped;
      }
    }
  }, [useDb, logActivity, events]);

  /**
   * Add a new payment.
   *
   * If Supabase is configured the payment is persisted to the DB and the
   * returned row (with its real DB id) is added to state. If the DB save
   * fails we throw — the caller can show a clear error instead of the
   * payment silently disappearing on the next refresh.
   *
   * When Supabase is not configured we fall back to in-memory only, using
   * a `pmt...` id so downstream code that checks `id.startsWith('temp_')`
   * (to detect failed saves) still works correctly.
   */
  const addPayment = useCallback(async (paymentData) => {
    const basePayment = {
      ...paymentData,
      payment_date: paymentData.payment_date || todayKey(),
      status: paymentData.status || 'completed',
    };

    let newPayment;
    if (useDb) {
      try {
        newPayment = await dbInsertPayment(basePayment);
      } catch (err) {
        console.error('[Supabase] insertPayment failed:', err);
        // Don't poison state with a temp record that will disappear on
        // the next fetch — surface the failure to the caller instead.
        throw err instanceof Error ? err : new Error(err?.message || 'Failed to save payment');
      }
    } else {
      newPayment = { id: `pmt${String(payments.length + 1).padStart(3, '0')}`, ...basePayment };
    }

    setPayments((prev) => [...prev, newPayment]);
    return newPayment;
  }, [useDb, payments.length]);

  /**
   * Update a payment
   */
  const updatePayment = useCallback(async (paymentId, updates) => {
    const existingPayment = payments.find((p) => p.id === paymentId);
    setPayments((prev) =>
      prev.map((payment) =>
        payment.id === paymentId ? { ...payment, ...updates } : payment
      )
    );
    if (useDb) {
      try {
        await updatePaymentDb(paymentId, updates);
      } catch (err) {
        console.error('[Supabase] updatePayment failed:', err.message);
        // Rollback — this is financial data; a failed correction must not
        // be left showing as applied while the database still has the old
        // figure, with nothing telling staff the save never happened.
        if (existingPayment) {
          setPayments((prev) =>
            prev.map((payment) => (payment.id === paymentId ? existingPayment : payment))
          );
        }
        throw new Error(err.message || 'Failed to save payment changes to database');
      }
    }
    return { id: paymentId, ...updates };
  }, [useDb, payments]);

  /**
   * Delete a payment
   */
  const deletePayment = useCallback(async (paymentId) => {
    const existingPayment = payments.find((p) => p.id === paymentId);
    setPayments((prev) => prev.filter((payment) => payment.id !== paymentId));
    if (useDb) {
      try {
        await deletePaymentDb(paymentId);
      } catch (err) {
        console.error('[Supabase] deletePayment failed:', err.message);
        if (existingPayment) {
          setPayments((prev) => [...prev, existingPayment]);
        }
        throw new Error(err.message || 'Failed to delete payment from database');
      }
    }
  }, [useDb, payments]);

  /**
   * Add a table booking
   */
  // ── Shared table booking email helpers ──
  const getVenueName = (venueId) => {
    const map = { reynards: 'Reynards Restaurant', randr: 'R&R', brewery: 'Brewery Bar' };
    return map[venueId] || venueId;
  };

  const formatBookingDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const buildBookingDetailsTable = (booking) => `
    <table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:20px;">
      <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;width:140px;">Date</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${formatBookingDate(booking.date)}</td></tr>
      <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Time</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.time || 'TBD'}</td></tr>
      <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Guests</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.covers || 0} covers</td></tr>
      <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Venue</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${getVenueName(booking.venue)}</td></tr>
      ${booking.roomNumber ? `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Hotel Room</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.roomNumber}</td></tr>` : ''}
    </table>`;

  const buildContactBox = () => `
    <div style="background:#F0FAF4;border:1px solid #BBF7D0;border-radius:8px;padding:16px;margin-bottom:20px;">
      <p style="font-size:14px;color:#1A5C3A;font-weight:600;margin-bottom:8px;">Need to make changes?</p>
      <p style="font-size:13px;color:#374151;margin:0;">
        To amend your booking details, please contact us directly:<br>
        <strong>Phone:</strong> 049 5556901<br>
        <strong>Email:</strong> <a href="mailto:info@errigalhotel.com" style="color:#1A5C3A;">info@errigalhotel.com</a>
      </p>
      <p style="font-size:13px;color:#6b7280;margin:8px 0 0;">
        If you need to cancel, please call us at least 24 hours in advance.
      </p>
    </div>
    <p style="font-size:13px;color:#6b7280;margin:0;">
      Errigal Country House Hotel &bull; Cootehill, Co. Cavan &bull; 049 5556901
    </p>`;

  // Send booking emails (works for both new and updated bookings)
  const sendTableBookingEmails = useCallback(async (booking, isUpdate = false) => {
    try {
      const dateFormatted = formatBookingDate(booking.date);
      const venueName = getVenueName(booking.venue);

      // 1. Hotel notification
      const hotelSubject = isUpdate
        ? `Table Booking Updated: ${booking.guestName} — ${dateFormatted}`
        : `New Table Booking: ${booking.guestName} — ${dateFormatted}`;
      const hotelHeading = isUpdate ? 'Table Booking Updated' : 'New Table Booking Received';
      const hotelBodyHtml = `
        <h2 style="color:#1A5C3A;margin-bottom:16px;">${hotelHeading}</h2>
        <table style="width:100%;border-collapse:collapse;font-size:14px;">
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;width:140px;">Guest Name</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.guestName || 'N/A'}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Date</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${dateFormatted}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Time</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.time || 'TBD'}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Covers</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.covers || 0}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Venue</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${venueName}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Phone</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${booking.phone || 'N/A'}</td></tr>
          <tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Email</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${booking.email || 'N/A'}</td></tr>
          ${booking.notes ? `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Notes</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${booking.notes}</td></tr>` : ''}
        </table>
        <p style="margin-top:20px;font-size:13px;color:#6b7280;">
          Log in to the <a href="https://errigal-events.vercel.app" style="color:#1A5C3A;font-weight:600;">Events Manager</a> to manage this booking.
        </p>
      `;
      await sendEmail({ to: 'info@errigalhotel.com', subject: hotelSubject, bodyHtml: hotelBodyHtml });

      // 2. Guest confirmation/update email
      if (booking.email) {
        const guestFirstName = (booking.guestName || 'Guest').split(' ')[0];
        const guestHeading = isUpdate ? 'Your Booking Has Been Updated' : 'Booking Confirmed';
        const guestIntro = isUpdate
          ? `Dear ${guestFirstName}, your reservation at Errigal Country House Hotel has been updated. Here are your new details:`
          : `Dear ${guestFirstName}, thank you for your reservation. We look forward to welcoming you!`;
        const guestSubject = isUpdate
          ? `Booking Updated — Errigal Country House Hotel — ${dateFormatted}`
          : `Your Table Booking — Errigal Country House Hotel — ${dateFormatted}`;

        const guestBodyHtml = `
          <h2 style="color:#1A5C3A;margin-bottom:8px;">${guestHeading}</h2>
          <p style="font-size:15px;color:#374151;margin-bottom:20px;">${guestIntro}</p>
          ${buildBookingDetailsTable(booking)}
          ${buildContactBox()}
        `;
        sendEmail({
          to: booking.email,
          subject: guestSubject,
          bodyHtml: guestBodyHtml,
          replyTo: 'info@errigalhotel.com',
        }).catch(err => console.warn('⚠️ Guest email failed:', err.message));
      }

      console.log(`✅ Table booking ${isUpdate ? 'update' : 'new'} emails sent for:`, booking.guestName);
    } catch (err) {
      console.error('⚠️ Table booking email failed (non-blocking):', err.message);
    }
  }, []);

  const addTableBooking = useCallback(async (bookingData) => {
    let newBooking;
    if (useDb) {
      try {
        newBooking = await dbInsertTableBooking(bookingData);
      } catch (err) {
        console.error('[Supabase] insertTableBooking failed:', err.message);
        // Do NOT fabricate a local booking here. That used to make a failed
        // save look identical to a real one on screen — the booking existed
        // nowhere but this one browser tab's memory, so it never reached
        // other PCs and vanished the moment this tab refreshed (the "made a
        // booking, it never showed up elsewhere, then it was gone" bug).
        // Surface the failure instead and let the caller keep the form open
        // so nothing the user typed is silently thrown away.
        return { _dbError: err.message, ...bookingData };
      }
    } else {
      newBooking = {
        id: `tbl${String(tableBookings.length + 1).padStart(3, '0')}`,
        ...bookingData,
      };
    }
    setTableBookings((prev) => [...prev, newBooking]);
    sendTableBookingEmails(newBooking, false);
    return newBooking;
  }, [useDb, tableBookings.length, sendTableBookingEmails]);

  /**
   * Add a new client
   */
  const addClient = useCallback(async (clientData) => {
    const baseClient = {
      ...clientData,
      totalEvents: clientData.totalEvents || 0,
      totalRevenue: clientData.totalRevenue || 0,
    };

    let newClient;
    if (useDb) {
      try {
        newClient = await dbInsertClient(baseClient);
      } catch (err) {
        console.error('[Supabase] insertClient failed:', err.message);
        // Previously fell back to a fake `temp_...` client that looked
        // like a real save to the caller — it would vanish for every
        // other PC and reappear as "missing" the moment this tab
        // reloaded. Throw instead so the caller can show a real error.
        throw new Error(err.message || 'Failed to save client to database');
      }
    } else {
      newClient = { id: Math.max(...clients.map((c) => typeof c.id === 'number' ? c.id : 0), 0) + 1, ...baseClient };
    }

    setClients((prev) => [...prev, newClient]);
    return newClient;
  }, [useDb, clients]);

  /**
   * Update an existing client
   */
  const updateClient = useCallback(async (clientId, updates) => {
    const existingClient = clients.find((c) => c.id === clientId);
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId ? { ...client, ...updates } : client
      )
    );
    if (useDb) {
      try {
        await updateClientDb(clientId, updates);
      } catch (err) {
        console.error('[Supabase] updateClient failed:', err.message);
        if (existingClient) {
          setClients((prev) =>
            prev.map((client) => (client.id === clientId ? existingClient : client))
          );
        }
        throw new Error(err.message || 'Failed to save client changes to database');
      }
    }
    return { id: clientId, ...updates };
  }, [useDb, clients]);

  /**
   * Delete a client
   */
  const deleteClient = useCallback(async (clientId) => {
    const existingClient = clients.find((c) => c.id === clientId);
    setClients((prev) => prev.filter((client) => client.id !== clientId));
    if (useDb) {
      try {
        await deleteClientDb(clientId);
      } catch (err) {
        console.error('[Supabase] deleteClient failed:', err.message);
        if (existingClient) {
          setClients((prev) => [...prev, existingClient]);
        }
        throw new Error(err.message || 'Failed to delete client from database');
      }
    }
  }, [useDb, clients]);

  /**
   * Add a note to an event
   */
  const addNote = useCallback(async (eventId, noteText) => {
    let newNotes = noteText;
    setEvents((prev) =>
      prev.map((evt) => {
        if (evt.id === eventId) {
          const currentNotes = evt.notes || '';
          newNotes = currentNotes ? `${currentNotes}\n---\n${noteText}` : noteText;
          return { ...evt, notes: newNotes };
        }
        return evt;
      })
    );

    if (useDb) {
      try {
        // Need to get current notes to build the update
        const evt = events.find((e) => e.id === eventId);
        const currentNotes = evt?.notes || '';
        const fullNotes = currentNotes ? `${currentNotes}\n---\n${noteText}` : noteText;
        await updateEventDb(eventId, { notes: fullNotes });
      } catch (err) {
        console.error('[Supabase] addNote failed:', err.message);
      }
    }

    return newNotes;
  }, [useDb, events]);

  /**
   * Update a table booking
   */
  const updateTableBooking = useCallback(async (bookingId, updates) => {
    // Get the full booking before updating (for email + rollback)
    const existingBooking = tableBookings.find(b => b.id === bookingId);

    setTableBookings((prev) =>
      prev.map((booking) =>
        booking.id === bookingId ? { ...booking, ...updates } : booking
      )
    );
    if (useDb) {
      try {
        await updateTableBookingDb(bookingId, updates);
      } catch (err) {
        console.error('[Supabase] updateTableBooking failed:', err.message);
        // Rollback optimistic update — without this, a failed save (e.g. a
        // "cancelled" status change) still LOOKED cancelled locally, could
        // still trigger a cancellation email to the guest, and would only
        // silently correct itself up to 3 minutes later via the safety-net
        // refetch, with nothing telling staff the save never happened.
        if (existingBooking) {
          setTableBookings((prev) =>
            prev.map((booking) => (booking.id === bookingId ? existingBooking : booking))
          );
        }
        throw new Error(err.message || 'Failed to save booking changes to database');
      }
    }

    // Send update email if meaningful details changed (not just status/table/notes changes)
    const detailFields = ['guestName', 'date', 'time', 'covers', 'venue', 'phone', 'email'];
    const hasDetailChange = detailFields.some(f => updates[f] !== undefined && existingBooking && updates[f] !== existingBooking[f]);
    if (hasDetailChange && existingBooking) {
      const updatedBooking = { ...existingBooking, ...updates };
      sendTableBookingEmails(updatedBooking, true);
    }

    return { id: bookingId, ...updates };
  }, [useDb, tableBookings, sendTableBookingEmails]);

  /**
   * Link a table booking to an event (upgrade to function)
   */
  const linkBookingToEvent = useCallback(async (bookingId, eventId) => {
    const existingBooking = tableBookings.find(b => b.id === bookingId);
    const updates = { linkedEventId: eventId, status: 'linked_to_event', linkedAt: new Date().toISOString() };
    setTableBookings((prev) =>
      prev.map((b) => (b.id === bookingId ? { ...b, ...updates } : b))
    );
    if (useDb) {
      try {
        await updateTableBookingDb(bookingId, updates);
      } catch (err) {
        console.error('[Supabase] linkBookingToEvent failed:', err.message);
        if (existingBooking) {
          setTableBookings((prev) =>
            prev.map((b) => (b.id === bookingId ? existingBooking : b))
          );
        }
        throw new Error(err.message || 'Failed to link booking to event in database');
      }
    }
  }, [useDb, tableBookings]);

  /**
   * Unlink a table booking from an event (revert to table)
   */
  const unlinkBookingFromEvent = useCallback(async (bookingId, revertStatus = 'booked') => {
    const existingBooking = tableBookings.find(b => b.id === bookingId);
    const updates = { linkedEventId: null, status: revertStatus, linkedAt: null };
    setTableBookings((prev) =>
      prev.map((b) => (b.id === bookingId ? { ...b, ...updates } : b))
    );
    if (useDb) {
      try {
        await updateTableBookingDb(bookingId, updates);
      } catch (err) {
        console.error('[Supabase] unlinkBookingFromEvent failed:', err.message);
        if (existingBooking) {
          setTableBookings((prev) =>
            prev.map((b) => (b.id === bookingId ? existingBooking : b))
          );
        }
        throw new Error(err.message || 'Failed to unlink booking from event in database');
      }
    }
  }, [useDb, tableBookings]);

  /**
   * Delete a table booking
   */
  const deleteTableBooking = useCallback(async (bookingId) => {
    const existingBooking = tableBookings.find(b => b.id === bookingId);
    setTableBookings((prev) => prev.filter((booking) => booking.id !== bookingId));
    if (useDb) {
      try {
        await deleteTableBookingDb(bookingId);
      } catch (err) {
        console.error('[Supabase] deleteTableBooking failed:', err.message);
        // Rollback: put it back rather than leave it "deleted" locally while
        // it still exists (and still occupies capacity) in the database.
        if (existingBooking) {
          setTableBookings((prev) => [...prev, existingBooking]);
        }
        throw new Error(err.message || 'Failed to delete booking from database');
      }
    }
  }, [useDb, tableBookings]);

  // ─── EVENT TASKS ───

  /**
   * Add a task (event-linked or standalone)
   */
  const addEventTask = useCallback(async (taskData) => {
    const tempId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const task = {
      id: tempId,
      eventId: taskData.eventId || null,
      title: taskData.title,
      assignedTo: taskData.assignedTo || null,
      assignedToName: taskData.assignedToName || '',
      createdBy: currentUser?.id || null,
      createdByName: currentUser?.name || '',
      dueDate: taskData.dueDate || null,
      completed: false,
      completedAt: null,
      priority: taskData.priority || 'normal',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setEventTasks((prev) => [task, ...prev]);

    // Add notification + email for the assignee (runs regardless of DB mode)
    if (taskData.assignedTo && taskData.assignedTo !== currentUser?.id) {
      addNotification({
        title: 'Task Assigned',
        message: `${currentUser?.name || 'Someone'} assigned you: "${taskData.title}"${taskData.eventId ? '' : ' (General)'}`,
        type: 'task_assigned',
        taskId: tempId,
        eventId: taskData.eventId,
      });

      // Send email notification to assignee
      const assigneeUser = availableUsers.find((u) => u.id === taskData.assignedTo);
      if (assigneeUser && assigneeUser.email) {
        const eventName = taskData.eventId
          ? (events.find((e) => e.id === taskData.eventId)?.name || 'an event')
          : null;
        const subject = eventName
          ? `Task assigned: ${taskData.title} — ${eventName}`
          : `Task assigned: ${taskData.title}`;
        const bodyHtml = `
          <p>Hi ${assigneeUser.name},</p>
          <p><strong>${currentUser?.name || 'Someone'}</strong> assigned you a task:</p>
          <div style="background: #f8fafc; border-left: 4px solid #10b981; padding: 12px 16px; margin: 16px 0; border-radius: 4px;">
            <p style="margin: 0 0 4px 0; font-weight: 600;">${taskData.title}</p>
            ${eventName ? `<p style="margin: 0 0 4px 0; color: #6b7280; font-size: 14px;">Event: ${eventName}</p>` : '<p style="margin: 0 0 4px 0; color: #6b7280; font-size: 14px;">General task (not linked to an event)</p>'}
            ${taskData.dueDate ? `<p style="margin: 0; color: #6b7280; font-size: 14px;">Due: ${taskData.dueDate}</p>` : ''}
          </div>
          <p><a href="https://errigal-events.vercel.app" style="color: #10b981; font-weight: 600;">Open Errigal Events Manager →</a></p>
        `;
        sendEmail({ to: assigneeUser.email, subject, bodyHtml }).catch((err) =>
          console.warn('[Email] Task notification failed:', err.message)
        );
      } else {
        console.warn('[Email] No email for assignee:', taskData.assignedTo, 'Available:', availableUsers.map(u => `${u.name}:${u.email}`));
      }
    }

    if (useDb) {
      try {
        const dbTask = await dbInsertEventTask(task);
        // Replace temp ID with real DB ID
        setEventTasks((prev) => prev.map((t) => (t.id === tempId ? dbTask : t)));
        return dbTask;
      } catch (err) {
        console.error('[Supabase] addEventTask failed:', err.message);
        // Remove optimistic insert on failure
        setEventTasks((prev) => prev.filter((t) => t.id !== tempId));
        throw err;
      }
    }

    return task;
  }, [useDb, currentUser, events, addNotification, availableUsers]);

  /**
   * Toggle task completion
   */
  const toggleEventTask = useCallback(async (taskId) => {
    let wasCompleted = false;
    setEventTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          wasCompleted = t.completed;
          return {
            ...t,
            completed: !t.completed,
            completedAt: !t.completed ? new Date().toISOString() : null,
          };
        }
        return t;
      })
    );

    if (useDb) {
      try {
        await updateEventTaskDb(taskId, {
          completed: !wasCompleted,
          completedAt: !wasCompleted ? new Date().toISOString() : null,
        });
      } catch (err) {
        console.error('[Supabase] toggleEventTask failed:', err.message);
        // Rollback
        setEventTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, completed: wasCompleted, completedAt: wasCompleted ? t.completedAt : null } : t))
        );
      }
    }
  }, [useDb]);

  /**
   * Update a task (title, assignee, due date, priority)
   */
  const updateEventTask = useCallback(async (taskId, updates) => {
    let previousTask = null;
    setEventTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          previousTask = t;
          return { ...t, ...updates, updatedAt: new Date().toISOString() };
        }
        return t;
      })
    );

    if (useDb) {
      try {
        await updateEventTaskDb(taskId, updates);
      } catch (err) {
        console.error('[Supabase] updateEventTask failed:', err.message);
        if (previousTask) {
          setEventTasks((prev) => prev.map((t) => (t.id === taskId ? previousTask : t)));
        }
        throw err;
      }
    }
  }, [useDb]);

  /**
   * Delete a task
   */
  const deleteEventTask = useCallback(async (taskId) => {
    setEventTasks((prev) => prev.filter((t) => t.id !== taskId));
    if (useDb) {
      try {
        await deleteEventTaskDb(taskId);
      } catch (err) {
        console.error('[Supabase] deleteEventTask failed:', err.message);
      }
    }
  }, [useDb]);

  // ─── AUTO TASKS: incomplete event details (Oct 2026) ───
  // Every live booking missing required details gets ONE open reception task
  // ("Complete event details"), due 7 days before the event, priority urgent
  // once inside the window. It closes itself as soon as the event is complete
  // (or archived / cancelled / past). Runs in whichever browser is open; the
  // DB unique index (migration 027) stops two browsers creating duplicates.
  // Each event/task is attempted once per session so a failure never loops.
  const detailTaskAttempted = useRef(new Set());
  useEffect(() => {
    if (!useDb || dbLoading || !isAuthenticated) return;
    const timer = setTimeout(async () => {
      const today = todayKey();
      const now = new Date().toISOString();
      const tried = detailTaskAttempted.current;
      const openAuto = new Map();
      eventTasks.forEach((t) => {
        if (t.autoType === DETAIL_TASK_TYPE && !t.completed && t.eventId) openAuto.set(t.eventId, t);
      });
      const eventsById = new Map(events.map((e) => [e.id, e]));

      for (const [eventId, task] of openAuto) {
        const evt = eventsById.get(eventId);
        const status = evt ? getEventDetailStatus(evt, today).status : 'none';
        if (status === 'booked' || status === 'music' || status === 'none') {
          const key = `close:${task.id}`;
          if (tried.has(key)) continue;
          tried.add(key);
          setEventTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: true, completedAt: now } : t)));
          try { await updateEventTaskDb(task.id, { completed: true, completedAt: now }); }
          catch (err) { console.warn('[AutoTask] close failed:', err.message); }
        } else if (status === 'urgent' && task.priority !== 'urgent') {
          const key = `urgent:${task.id}`;
          if (tried.has(key)) continue;
          tried.add(key);
          setEventTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, priority: 'urgent' } : t)));
          try { await updateEventTaskDb(task.id, { priority: 'urgent' }); }
          catch (err) { console.warn('[AutoTask] priority update failed:', err.message); }
        }
      }

      for (const evt of events) {
        if (!evt?.id || openAuto.has(evt.id)) continue;
        const st = getEventDetailStatus(evt, today);
        if (st.status !== 'provisional' && st.status !== 'urgent') continue;
        const key = `open:${evt.id}`;
        if (tried.has(key)) continue;
        tried.add(key);
        try {
          const dbTask = await dbInsertEventTask({
            eventId: evt.id,
            title: 'Reception: complete event details for kitchen & operations',
            assignedTo: null,
            assignedToName: 'Reception',
            createdBy: null,
            createdByName: 'System',
            dueDate: detailTaskDueDate(evt.date, today),
            completed: false,
            completedAt: null,
            priority: st.status === 'urgent' ? 'urgent' : 'normal',
            autoType: DETAIL_TASK_TYPE,
          });
          setEventTasks((prev) => (prev.some((t) => t.id === dbTask.id) ? prev : [dbTask, ...prev]));
        } catch (err) {
          // 23505 = another browser already created it — fine.
          if (err?.code !== '23505') console.warn('[AutoTask] create failed:', err.message);
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [useDb, dbLoading, isAuthenticated, events, eventTasks]);

  // ─── SPA BOOKINGS CRUD ───
  // Manual entries created by reception so the daily function sheet shows
  // spa appointments alongside events for service staff who don't have the
  // app open during shifts.
  const addSpaBooking = useCallback(async (bookingData) => {
    const optimistic = {
      id: `spa_${Date.now()}`,
      date: bookingData.date || null,
      time: bookingData.time || '',
      guestName: bookingData.guestName || '',
      details: bookingData.details || '',
      covers: bookingData.covers ?? null,
      deposit: bookingData.deposit ?? null,
      comments: bookingData.comments || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSpaBookings((prev) => [optimistic, ...prev]);
    if (useDb) {
      try {
        const saved = await dbInsertSpaBooking(bookingData);
        setSpaBookings((prev) => prev.map((b) => (b.id === optimistic.id ? saved : b)));
        return saved;
      } catch (err) {
        console.error('[Supabase] addSpaBooking failed:', err.message);
        setSpaBookings((prev) => prev.filter((b) => b.id !== optimistic.id));
        throw err;
      }
    }
    return optimistic;
  }, [useDb]);

  const updateSpaBooking = useCallback(async (bookingId, updates) => {
    setSpaBookings((prev) =>
      prev.map((b) => (b.id === bookingId ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b))
    );
    if (useDb) {
      try {
        await updateSpaBookingDb(bookingId, updates);
      } catch (err) {
        console.error('[Supabase] updateSpaBooking failed:', err.message);
      }
    }
  }, [useDb]);

  const deleteSpaBooking = useCallback(async (bookingId) => {
    setSpaBookings((prev) => prev.filter((b) => b.id !== bookingId));
    if (useDb) {
      try {
        await deleteSpaBookingDb(bookingId);
      } catch (err) {
        console.error('[Supabase] deleteSpaBooking failed:', err.message);
      }
    }
  }, [useDb]);

  /**
   * Add a sent email to the history
   */
  const addSentEmail = useCallback((emailRecord) => {
    setSentEmails((prev) => [emailRecord, ...prev]);

    // Persist to Supabase
    if (useDb) {
      dbInsertSentEmail(emailRecord).catch((err) =>
        console.warn('[Supabase] Failed to save sent email:', err.message)
      );
    }
  }, [useDb]);

  /**
   * Process and send a workflow email
   */
  const processWorkflowEmail = useCallback(async (action, event, template) => {
    const venue = ROOMS.find((r) => r.id === event.venue_id);
    const variables = buildVariables(event, venue);
    // Subject is plain-text email header; body is HTML (default escaped).
    const subject = replaceVariables(template.subject, variables, { escape: false });
    const body = replaceVariables(template.body, variables);
    const html = generateBrandedEmail(body);

    const result = await sendEmail({
      to: event.clientEmail || event.email || '',
      subject,
      bodyHtml: html,
      bodyText: body,
    });

    const emailRecord = {
      id: `email_${Date.now()}`,
      eventId: event.id,
      eventName: event.name,
      templateId: template.id,
      templateName: template.name,
      to: event.clientEmail || event.email || '',
      subject,
      body: html,
      sentAt: new Date().toISOString(),
      status: result.status === 'sent' ? 'sent' : 'failed',
      auto: true,
      // Save the server-generated tracking token so opens/clicks ping back
      trackingId: result.trackingToken || '',
      tracking: { opens: 0, clicks: 0, events: [] },
    };

    addSentEmail(emailRecord);
    return result;
  }, [addSentEmail]);

  /**
   * Send email from review modal
   */
  const sendReviewedEmail = useCallback(async (emailData) => {
    const { to, subject, body, attachments, fileAttachments, template, event, workflow } = emailData;
    const html = generateBrandedEmail(body.replace(/\n/g, '<br>'), subject);

    const result = await sendEmail({
      to,
      subject,
      bodyHtml: body.replace(/\n/g, '<br>'),
      bodyText: body,
      attachments: fileAttachments || [],
    });

    // Prefer the server-generated tracking token (UUID). Fall back to a
    // local random ID only if the server didn't return one (demo mode /
    // send failure) — the tracking pixel will just never resolve in that
    // case, which is fine.
    const trackingId = result.trackingToken || `trk_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    const emailRecord = {
      id: `email_${Date.now()}`,
      trackingId,
      eventId: event.id,
      eventName: event.name,
      templateId: template?.id || 'manual',
      templateName: template?.name || 'Manual Email',
      to,
      subject,
      body,
      sentAt: new Date().toISOString(),
      status: result.status === 'sent' ? 'sent' : 'failed',
      auto: false,
      reviewed: true,
      attachments: attachments || [],
      tracking: {
        opens: 0,
        lastOpened: null,
        clicks: 0,
        lastClicked: null,
        events: [],
      },
    };

    addSentEmail(emailRecord);
    logActivity(`sent email "${subject}" to ${to}`);

    setEmailTracking((prev) => ({
      ...prev,
      [trackingId]: {
        emailId: emailRecord.id,
        eventId: event.id,
        opens: 0,
        clicks: 0,
        events: [],
      },
    }));

    return result;
  }, [addSentEmail, logActivity]);

  /**
   * Record email tracking event
   */
  const recordTrackingEvent = useCallback((emailId, type) => {
    const timestamp = new Date().toISOString();

    setSentEmails((prev) =>
      prev.map((email) => {
        if (email.id !== emailId) return email;
        const tracking = email.tracking || { opens: 0, clicks: 0, lastOpened: null, lastClicked: null, events: [] };
        return {
          ...email,
          tracking: {
            ...tracking,
            opens: type === 'open' ? tracking.opens + 1 : tracking.opens,
            clicks: type === 'click' ? tracking.clicks + 1 : tracking.clicks,
            lastOpened: type === 'open' ? timestamp : tracking.lastOpened,
            lastClicked: type === 'click' ? timestamp : tracking.lastClicked,
            events: [...(tracking.events || []), { type, timestamp }],
          },
        };
      })
    );

    setEmailNotifications((prev) => [
      {
        id: `notif_${Date.now()}`,
        emailId,
        type,
        timestamp,
        read: false,
      },
      ...prev,
    ].slice(0, 50));
  }, []);

  /**
   * Dismiss a notification
   */
  const dismissNotification = useCallback((notifId) => {
    setEmailNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
    );
    setAllNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
    );
  }, []);

  const deleteNotification = useCallback((notifId) => {
    setAllNotifications((prev) => prev.filter((n) => n.id !== notifId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setAllNotifications([]);
  }, []);

  /**
   * Toggle event email override
   */
  const toggleEventEmailOverride = useCallback((eventId, workflowId, disabled) => {
    const key = `${eventId}_${workflowId}`;
    setEventEmailOverrides((prev) => ({
      ...prev,
      [key]: { disabled, updatedAt: new Date().toISOString() },
    }));
  }, []);

  /**
   * Trigger a workflow email with cancel window
   */
  const triggerWorkflowEmail = useCallback((event, workflow, template) => {
    if (!workflowsEnabled || !template?.enabled) return;

    const toastId = `toast_${Date.now()}`;
    const cancelWindowMs = (workflow.cancelWindowSeconds || 10) * 1000;

    setEmailToasts((prev) => [
      ...prev,
      {
        id: toastId,
        status: 'pending',
        templateName: template.name,
        eventName: event.name,
        to: event.clientEmail || event.email || '',
        cancelWindowMs,
        createdAt: Date.now(),
      },
    ]);

    const timer = setTimeout(async () => {
      setEmailToasts((prev) =>
        prev.map((t) => (t.id === toastId ? { ...t, status: 'sending' } : t))
      );

      const result = await processWorkflowEmail(
        createPendingEmailAction(workflow, event, template),
        event,
        template
      );

      setEmailToasts((prev) =>
        prev.map((t) =>
          t.id === toastId
            ? { ...t, status: result.status === 'sent' ? 'sent' : 'failed' }
            : t
        )
      );

      setTimeout(() => {
        setEmailToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, 3000);

      delete pendingTimers.current[toastId];
    }, cancelWindowMs);

    pendingTimers.current[toastId] = timer;
  }, [workflowsEnabled, processWorkflowEmail]);

  /**
   * Cancel a pending workflow email
   */
  const cancelWorkflowEmail = useCallback((toastId) => {
    if (pendingTimers.current[toastId]) {
      clearTimeout(pendingTimers.current[toastId]);
      delete pendingTimers.current[toastId];
    }
    setEmailToasts((prev) =>
      prev.map((t) => (t.id === toastId ? { ...t, status: 'cancelled' } : t))
    );
    setTimeout(() => {
      setEmailToasts((prev) => prev.filter((t) => t.id !== toastId));
    }, 2000);
  }, []);

  /**
   * Dismiss a toast
   */
  const dismissToast = useCallback((toastId) => {
    setEmailToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  /**
   * Update event stage with workflow email trigger
   */
  const updateEventStageWithWorkflow = useCallback(async (eventId, newStage) => {
    const event = events.find((e) => e.id === eventId);
    if (!event) return;
    const previousStage = event.stage;

    // Update the stage (optimistic)
    setEvents((prev) =>
      prev.map((evt) =>
        evt.id === eventId ? { ...evt, stage: newStage } : evt
      )
    );

    // Persist to DB
    if (useDb) {
      try {
        await updateEventDb(eventId, { stage: newStage });
      } catch (err) {
        console.error('[Supabase] updateEventStage failed:', err.message);
        // Rollback — without this the card stayed in the new stage forever
        // on this PC with no error shown, while the database (and every
        // other PC) still had the old stage.
        setEvents((prev) =>
          prev.map((evt) => (evt.id === eventId ? { ...evt, stage: previousStage } : evt))
        );
        throw new Error(err.message || 'Failed to save stage change to database');
      }
    }

    // Check for workflow trigger — only once the stage change is confirmed
    // saved, so a failed save can't still fire a "you've been confirmed"
    // style email for a move that never actually happened.
    if (workflowsEnabled) {
      const workflow = getWorkflowForStageChange(newStage, event.type);
      if (workflow) {
        const template = emailTemplates.find((t) => t.id === workflow.templateId);
        if (template && template.enabled !== false) {
          triggerWorkflowEmail(event, workflow, template);
        }
      }
    }

    return { id: eventId, stage: newStage };
  }, [useDb, events, workflowsEnabled, emailTemplates, triggerWorkflowEmail]);

  // ─── WEDDING AUTO-ALERTS ───
  // Check wedding events for upcoming milestones and generate notifications
  useEffect(() => {
    if (!events || events.length === 0) return;
    const now = new Date();
    const weddings = events.filter((e) => e.type?.toLowerCase() === 'wedding' && e.date);
    const alertsGenerated = [];

    weddings.forEach((w) => {
      const weddingDate = new Date(w.date);
      const daysUntil = Math.ceil((weddingDate - now) / (1000 * 60 * 60 * 24));
      const dismissed = w.alertsDismissed || [];

      // 1. Provisional date follow-up: 2 weeks after entering provisional_date stage
      if (w.stage === 'provisional_date') {
        const alertKey = `followup_${w.id}`;
        if (!dismissed.includes(alertKey)) {
          // Check if event has been in provisional_date for 14+ days
          const updatedDate = new Date(w.updated_at || w.created_at);
          const daysSinceUpdate = Math.ceil((now - updatedDate) / (1000 * 60 * 60 * 24));
          if (daysSinceUpdate >= 14) {
            alertsGenerated.push({ key: alertKey, title: `Follow up: ${w.name}`, message: `Provisional date set over 2 weeks ago — time to follow up`, type: 'wedding_alert', eventId: w.id });
          }
        }
      }

      // 2. Food tasting: 8 weeks (56 days) before wedding, after deposit paid
      if (['deposit_paid', 'food_tasting'].includes(w.stage) && daysUntil <= 56 && daysUntil > 0) {
        const alertKey = `tasting_${w.id}`;
        if (!dismissed.includes(alertKey)) {
          alertsGenerated.push({ key: alertKey, title: `Food tasting due: ${w.name}`, message: `Wedding is ${daysUntil} days away — schedule food tasting & collect details`, type: 'wedding_alert', eventId: w.id });
        }
      }

      // 3. First payment €2.5k: 6 months (180 days) before wedding
      if (['food_tasting', 'first_payment'].includes(w.stage) && daysUntil <= 180 && daysUntil > 0) {
        const alertKey = `first_payment_${w.id}`;
        if (!dismissed.includes(alertKey)) {
          alertsGenerated.push({ key: alertKey, title: `1st payment due: ${w.name}`, message: `Wedding is ${daysUntil} days away — €2,500 payment due`, type: 'wedding_alert', eventId: w.id });
        }
      }

      // 4. Second payment 75%: 2 weeks (14 days) before wedding
      if (['first_payment', 'second_payment'].includes(w.stage) && daysUntil <= 14 && daysUntil > 0) {
        const alertKey = `second_payment_${w.id}`;
        if (!dismissed.includes(alertKey)) {
          alertsGenerated.push({ key: alertKey, title: `Final payment due: ${w.name}`, message: `Wedding is ${daysUntil} days away — 75% balance payment due`, type: 'wedding_alert', eventId: w.id });
        }
      }
    });

    // Only add alerts that don't already exist in notifications
    alertsGenerated.forEach((alert) => {
      const exists = allNotifications.some((n) => n.alertKey === alert.key);
      if (!exists) {
        const notif = {
          id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          title: alert.title,
          message: alert.message,
          type: alert.type,
          eventId: alert.eventId,
          alertKey: alert.key,
          read: false,
          createdAt: new Date().toISOString(),
          time: new Date().toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }),
        };
        setAllNotifications((prev) => [notif, ...prev].slice(0, 100));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length]); // Only re-check when events count changes or on mount

  /**
   * Trigger the fetch-emails Edge Function to pull any NEW messages from
   * IMAP (Maxer), then refresh our local state from the DB. Unlike the
   * 2-minute poll (which only reads the DB), this is the action behind the
   * "Refresh" button in the inbox — use it when the user has just sent a
   * test and wants to see it immediately.
   *
   * Returns the count of emails now in state, or rethrows on failure.
   */
  const refreshInboundEmails = useCallback(async () => {
    if (!useDb) return inboundEmails.length;
    // 1. Ask the Edge Function to hit IMAP and upsert any new rows.
    try {
      await triggerInboundFetch({ refresh: true, maxResults: 50 });
    } catch (err) {
      console.warn('[Refresh] Edge function call failed, will still re-read DB:', err?.message);
    }
    // 2. Re-read from the DB whether or not the Edge call worked —
    //    someone else may have refreshed in the meantime.
    const emails = await fetchInboundEmails();
    setInboundEmails(emails);
    setUnreadEmailCount(emails.filter((e) => !e.is_read).length);
    // 3. Also refresh the sent-emails log so open/click counters (updated
    //    server-side by the track-email function) show up without a page
    //    reload. Best-effort: don't block the inbox refresh on failures.
    try {
      const sent = await fetchSentEmails();
      if (Array.isArray(sent)) setSentEmails(sent);
    } catch (err) {
      console.warn('[Refresh] sent-emails re-read failed:', err?.message);
    }
    return emails.length;
  }, [useDb, inboundEmails.length]);

  /**
   * Mark an inbound email as read
   */
  const markEmailRead = useCallback(async (emailId) => {
    setInboundEmails(prev => prev.map(e =>
      e.id === emailId ? { ...e, is_read: true, isRead: true } : e
    ));
    setUnreadEmailCount(prev => Math.max(0, prev - 1));
    if (useDb) {
      markInboundEmailRead(emailId).catch(err =>
        console.warn('[Supabase] Failed to mark email read:', err.message)
      );
    }
  }, [useDb]);

  /**
   * Link an inbound email to an existing event. Updates both the DB and
   * local state so the email's linked badge appears immediately.
   */
  const linkEmailToEvent = useCallback(async (emailId, eventId) => {
    if (!emailId || !eventId) return;
    setInboundEmails(prev => prev.map(e =>
      e.id === emailId
        ? { ...e, processedEventId: eventId, eventId, processed_event_id: eventId, isProcessed: true, is_processed: true }
        : e
    ));
    if (useDb) {
      try { await linkInboundEmailToEvent(emailId, eventId); }
      catch (err) { console.error('[Supabase] linkInboundEmailToEvent failed:', err.message); throw err; }
    }
  }, [useDb]);

  /**
   * Link an inbound email to an existing table booking (migration 015).
   */
  const linkEmailToBooking = useCallback(async (emailId, bookingId) => {
    if (!emailId || !bookingId) return;
    setInboundEmails(prev => prev.map(e =>
      e.id === emailId
        ? { ...e, processedBookingId: bookingId, bookingId, processed_booking_id: bookingId, isProcessed: true, is_processed: true }
        : e
    ));
    if (useDb) {
      try { await linkInboundEmailToBooking(emailId, bookingId); }
      catch (err) { console.error('[Supabase] linkInboundEmailToBooking failed:', err.message); throw err; }
    }
  }, [useDb]);

  /**
   * Archive an inbound email — hides it from the active inbox but keeps
   * the row for audit. Returns to active on unarchive (TODO: unarchive UI).
   */
  const archiveEmail = useCallback(async (emailId) => {
    if (!emailId) return;
    setInboundEmails(prev => prev.map(e =>
      e.id === emailId ? { ...e, isProcessed: true, is_processed: true } : e
    ));
    if (useDb) {
      try { await archiveInboundEmail(emailId); }
      catch (err) { console.error('[Supabase] archiveInboundEmail failed:', err.message); throw err; }
    }
  }, [useDb]);

  // ─── RESTAURANT PACKAGES ───
  const addRestaurantPackage = useCallback(async (pkg) => {
    const saved = await dbInsertRestaurantPackage(pkg);
    setRestaurantPackages((prev) => [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder));
    return saved;
  }, []);

  const updateRestaurantPackage = useCallback(async (id, updates) => {
    const saved = await dbUpdateRestaurantPackage(id, updates);
    setRestaurantPackages((prev) => prev.map((p) => (p.id === id ? saved : p)).sort((a, b) => a.sortOrder - b.sortOrder));
    return saved;
  }, []);

  const value = {
    // Database status
    dbLoading,
    dbError,
    retryLoadData,
    useDb,

    // User session
    currentUser,
    switchUser,
    availableUsers,

    // Notifications
    notifications,
    addNotification,
    dismissNotification,
    deleteNotification,
    clearAllNotifications,

    // Activity log (admin)
    activityLog,
    logActivity,

    // State
    events,
    setEvents,
    clients,
    setClients,
    venues: ROOMS,
    tableBookings,
    setTableBookings,
    payments,
    setPayments,
    invoices,
    setInvoices,
    emailTemplates,
    setEmailTemplates,

    // Event helpers
    addEvent,
    updateEvent,
    updateEventStage,
    deleteEvent,

    // Payment helpers
    addPayment,
    updatePayment,
    deletePayment,

    // Table booking helpers
    addTableBooking,
    updateTableBooking,
    deleteTableBooking,
    linkBookingToEvent,
    unlinkBookingFromEvent,

    // Event tasks
    eventTasks,
    addEventTask,
    toggleEventTask,
    updateEventTask,
    deleteEventTask,

    // Spa bookings (manual reception entries surfaced on the daily sheet)
    spaBookings,
    addSpaBooking,
    updateSpaBooking,
    deleteSpaBooking,

    // Restaurant packages
    restaurantPackages,
    addRestaurantPackage,
    updateRestaurantPackage,

    // Client helpers
    addClient,
    updateClient,
    deleteClient,

    // General helpers
    addNote,

    // Email automation
    sentEmails,
    emailToasts,
    workflowsEnabled,
    setWorkflowsEnabled,
    addSentEmail,
    processWorkflowEmail,
    triggerWorkflowEmail,
    cancelWorkflowEmail,
    dismissToast,
    updateEventStageWithWorkflow,

    // Review-based email system
    sendReviewedEmail,
    emailTracking,
    emailNotifications,
    recordTrackingEvent,
    upcomingReminders,
    eventEmailOverrides,
    toggleEventEmailOverride,

    // Inbound emails
    inboundEmails,
    unreadEmailCount,
    markEmailRead,
    linkEmailToEvent,
    linkEmailToBooking,
    archiveEmail,
    refreshInboundEmails,
    setInboundEmails,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * Custom hook to use AppContext
 */
export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppContextProvider');
  }
  return context;
};
